/**
 * errorText.js — human copy for the backend's error codes.
 *
 * Split out of api.js so it can be EXECUTED. api.js imports config and auth
 * and is bound to the browser (fetch, import.meta.env); this is a pure map and
 * a lookup, which means `node src/capacity.test.js` can assert that the codes
 * the Engine screen can produce actually have copy. Copy nobody can test is
 * copy that silently goes missing.
 *
 * Anything unmapped falls back to the backend's own `message`, which is
 * written to be readable — so an unmapped code degrades to a real sentence
 * rather than to nothing.
 */

export function errorText(err) {
  if (!err) return '';
  const map = {
    forbidden: 'Superuser access is required for this.',
    org_exists: 'An organisation with that name already exists.',
    email_exists: 'That email is already provisioned.',
    org_not_found: 'That organisation no longer exists.',
    user_not_found: 'That user no longer exists.',
    not_bound: 'This account has no Google account bound to unbind.',
    self_lockout: 'You cannot demote or deactivate yourself.',
    bad_role: 'Role must be operator, approver or admin.',
    db_unconfigured: 'The backend has no database configured.',
    sso_unconfigured: 'Google sign-in is not configured on the backend.',
    invalid_token: 'Your session expired. Sign in again.',
    god_required: 'Only a god can change platform access.',
    last_god_org: 'This org holds the only platform owner who can still sign in — promote another god first.',
    unsupported_image: 'Logo must be a PNG, JPEG or WebP image (SVG is not accepted).',
    file_too_large: 'That image is over the 512 KB limit.',
    blob_unconfigured: 'The backend has no blob store configured, so logos cannot be stored.',
    empty_file: 'That file is empty.',
    // Engine policy (contract 1.7 / K5). Both of these are refusals BY NAME
    // rather than silent drops, so the backend's own message names the
    // parameter and says why — which is more specific than any copy written
    // here could be. Mapped anyway so an unmapped code never renders raw.
    EnginePolicyNotAccepted:
      'That setting is resolved by the server for this deployment and cannot '
      + 'be sent with a request.',
    EngineConfigNotSettablePerOrg:
      'That setting cannot be set per organisation — see the reason below.',
    // The ranking-cache card (contract 1.9). `ranking_cache_unlocked` is a
    // deploy skew: the installed ranking cache has no lock, so clearing it
    // during exports could fail one, and the backend refuses rather than try.
    ranking_cache_unlocked:
      'This backend\'s engine package predates the safe ranking-cache clear, so '
      + 'it refuses to clear while exports may be running. Deploy the newer '
      + 'engine package first.',
    PrepressUnavailable: 'Prepress is not installed on this backend, so there '
      + 'is no ranking cache to show or clear.',
    // The Flap slabs tab (contract 1.13, V2 #54). `flap_slabs_invalid` is
    // shown WITH the backend's message by flapSlabs.saveErrorText — that
    // message names the row and the rule, which no copy here could.
    flap_slabs_invalid: 'The flap-slab table was not saved: one of its rows '
      + 'breaks a rule.',
    flap_slabs_conflict: 'Someone else saved the flap-slab table after you '
      + 'opened it. Reload it, make your change again, then save.',
    flap_slabs_unsupported: 'This backend\'s engine package cannot apply a '
      + 'flap-slab table yet. Deploy the newer engine package first.',
  };
  if (err.code && map[err.code]) return map[err.code];
  if (err.message && err.message !== err.code) return err.message;
  if (err.status) return `The backend returned ${err.status}.`;
  return 'Something went wrong.';
}
